import React, { useLayoutEffect, useRef, useState } from 'react'
import { Check, Loader2, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import ExtraWheelFace, { TITAN_LOGO_DESIGN } from '../../../pages/playground/ExtraWheelFaces'

const IDLE_TURN_DURATION_MS = 10000
const NEAR_MISS_FREQUENCY = 0.2
const NEAR_MISS_MIN_DISTANCE_DEGREES = 3
const NEAR_MISS_MAX_DISTANCE_DEGREES = 10
const GLOW_TONES = { idle: 'ice', spin: 'solar', win: 'emerald', loss: 'laser' }

const UpgradeDial = ({ chance, spin, loading, outcome, emptyInventory = false, guest = false }) => {
  const { t } = useTranslation()
  const [rotation, setRotation] = useState({ pointer: 0, dial: 0 })
  const pointerRef = useRef(null)
  const idleAngleRef = useRef(0)
  const [settledChance, setSettledChance] = useState(null)
  const showingResult = loading === 'result' && outcome
  const glow = showingResult ? (outcome.win ? 'win' : 'loss') : spin || loading === 'spin' ? 'spin' : loading && loading !== 'reset' ? 'preparing' : 'idle'
  const displayedChance = spin?.chance ?? (showingResult ? settledChance : chance)
  const status = guest ? t('guest.privateDescription') : showingResult ? t(outcome.win ? 'upgrader.step5Success' : 'upgrader.step5Fail')
    : loading === 'request' || loading === 'spin' ? t('upgraderNew.upgrading')
      : loading ? t('common.loading') : emptyInventory ? t('upgrader.noItems') : displayedChance === null ? t('upgrader.step2SelectTarget') : ''
  const centerContent = (
    <AnimatePresence mode="wait" initial={false}>
      {showingResult ? (
        <motion.span
          key={outcome.win ? 'win' : 'fail'}
          aria-hidden="true"
          initial={{ scale: 0.5, opacity: 0, rotate: outcome.win ? -25 : 25 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          exit={{ scale: 0.85, opacity: 0, transition: { duration: 0.22 } }}
          transition={{ type: 'spring', stiffness: 300, damping: 18 }}
          className={`relative z-10 flex h-16 w-16 items-center justify-center rounded-full border ${outcome.win ? 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary shadow-[0_0_24px_rgba(0,255,127,0.22)]' : 'border-error/40 bg-error/10 text-error shadow-[0_0_24px_rgba(255,75,75,0.22)]'}`}
        >
          {outcome.win ? <Check className="h-10 w-10" strokeWidth={3} /> : <X className="h-10 w-10" strokeWidth={3} />}
        </motion.span>
      ) : loading && loading !== 'spin' && loading !== 'reset' ? (
        <motion.span key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="relative z-10">
          <Loader2 aria-hidden="true" className="h-12 w-12 text-text-secondary animate-spin" />
        </motion.span>
      ) : emptyInventory && displayedChance == null ? null : (
        <motion.span key="chance" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} transition={{ duration: 0.22 }} className="relative z-10 text-[clamp(2.5rem,11vw,3.4rem)] font-black leading-none tabular-nums tracking-tight text-white">
          {displayedChance == null ? '—' : Math.round(displayedChance)}{displayedChance != null && <span className="ml-0.5 text-xl" style={{ color: TITAN_LOGO_DESIGN.accent }}>%</span>}
        </motion.span>
      )}
    </AnimatePresence>
  )

  useLayoutEffect(() => {
    if (spin || ['request', 'spin', 'result', 'reset'].includes(loading)) return undefined
    let frame
    let lastTime = null
    const advance = (time) => {
      if (lastTime !== null) {
        idleAngleRef.current = (idleAngleRef.current + (time - lastTime) * 360 / IDLE_TURN_DURATION_MS) % 360
        if (pointerRef.current) pointerRef.current.style.transform = `rotate(${idleAngleRef.current}deg)`
      }
      lastTime = time
      frame = requestAnimationFrame(advance)
    }
    frame = requestAnimationFrame(advance)
    return () => cancelAnimationFrame(frame)
  }, [spin, loading])

  useLayoutEffect(() => {
    if (!spin) {
      if (loading === 'reset') {
        setRotation((current) => ({
          pointer: Math.round(current.pointer / 360) * 360,
          dial: Math.round(current.dial / 360) * 360
        }))
        idleAngleRef.current = 0
      }
      return undefined
    }
    setSettledChance(spin.chance)
    const greenAngle = spin.chance * 3.6
    const sectorStart = spin.win ? 0 : greenAngle
    const sectorWidth = spin.win ? greenAngle : 360 - greenAngle
    const nearMiss = greenAngle > 0 && greenAngle < 360 && Math.random() < NEAR_MISS_FREQUENCY
    const distance = nearMiss
      ? Math.min(NEAR_MISS_MIN_DISTANCE_DEGREES + Math.random() * (NEAR_MISS_MAX_DISTANCE_DEGREES - NEAR_MISS_MIN_DISTANCE_DEGREES), sectorWidth / 2)
      : 0
    const angle = nearMiss ? greenAngle + (spin.win ? -distance : distance)
      : sectorStart + sectorWidth * (0.05 + Math.random() * 0.9)
    const startAngle = idleAngleRef.current
    setRotation((current) => ({ ...current, pointer: startAngle }))
    const frame = requestAnimationFrame(() => setRotation((current) => ({
      pointer: startAngle + 720 + (angle / 2 - startAngle + 360) % 360,
      dial: current.dial - 720 - (current.dial % 360 + angle / 2 + 360) % 360
    })))
    return () => cancelAnimationFrame(frame)
  }, [spin, loading])

  return (
    <section aria-label={t('upgrader.upgradeChance')} className="py-1">
      <div className="upgrade-dial-glow pg-glow__stage relative mx-auto flex aspect-square w-[min(66vw,256px)] max-w-full items-center justify-center rounded-full lg:w-[min(32vw,360px)]" data-glow={glow}>
        {Object.entries(GLOW_TONES).map(([tone, variant]) => (
          <span key={tone} aria-hidden="true" className={`upgrade-dial-glow__layer upgrade-dial-glow__layer--${tone} pg-glow--${variant} pg-glow--active`}>
            <span className="pg-glow__aura" />
            <span className="pg-glow__ring" />
          </span>
        ))}
        <div className="relative z-10 h-full w-full rounded-full" style={{ boxShadow: `0 0 40px -12px ${TITAN_LOGO_DESIGN.glow}, 0 20px 25px -14px #000` }}>
          <ExtraWheelFace design={TITAN_LOGO_DESIGN} chance={displayedChance} rotation={rotation.pointer} dialRotation={rotation.dial} spinning={Boolean(spin)} returning={loading === 'reset'} spinDuration={spin?.duration} pointerRef={pointerRef} selected centerContent={centerContent} />
        </div>
      </div>
      <span role="status" aria-live="polite" className="sr-only">{status}</span>
    </section>
  )
}

export default UpgradeDial
